import React, { useEffect, useMemo, useState } from 'react';
import './EmlApercu.css';

// Aperçu d'un e-mail enregistré (.eml) dans la visionneuse de documents.
// Le fichier est lu DANS LE NAVIGATEUR (postal-mime, chargée à la demande) :
// rien ne part vers un service tiers, comme l'aperçu Excel de Garantie.
//
// Sécurité : le corps HTML d'un e-mail transmis est du contenu étranger. Il est
// affiché dans une iframe `sandbox` SANS `allow-scripts` ni `allow-same-origin`
// (aucun script, aucun accès à la session CRM), avec une CSP qui bloque tout
// chargement extérieur. Les images distantes (souvent des pixels de suivi qui
// préviennent l'expéditeur de l'ouverture) restent masquées tant que
// l'utilisateur ne les demande pas ; les images intégrées (cid:) s'affichent.

export const estEml = (doc) => {
  const type = (doc?.type || doc?.type_mime || '').toLowerCase();
  const nom = (doc?.name || doc?.nom || '').toLowerCase();
  return type === 'message/rfc822' || nom.endsWith('.eml');
};

const enBase64 = (buffer) => {
  const octets = new Uint8Array(buffer);
  let s = '';
  for (let i = 0; i < octets.length; i += 0x8000) s += String.fromCharCode.apply(null, octets.subarray(i, i + 0x8000));
  return btoa(s);
};

const echapper = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const adresse = (a) => {
  if (!a) return '';
  if (a.group) return a.group.map(adresse).join(', ');
  return a.name ? `${a.name} <${a.address || ''}>` : (a.address || '');
};
const adresses = (liste) => (Array.isArray(liste) ? liste : liste ? [liste] : []).map(adresse).filter(Boolean).join(', ');

const dateLisible = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' });
};

const taille = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1).replace('.', ',')} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

function corpsIframe(mail, imagesDistantes) {
  // Images intégrées : cid:xxx → data: (seule source d'image admise par la CSP
  // tant que les images distantes ne sont pas demandées).
  const parCid = new Map();
  for (const pj of mail.attachments || []) {
    if (pj.contentId) parCid.set(pj.contentId.replace(/^<|>$/g, '').toLowerCase(), pj);
  }
  let html = mail.html;
  if (html) {
    html = html.replace(/cid:([^"'\s)>]+)/gi, (m, id) => {
      const pj = parCid.get(decodeURIComponent(id).toLowerCase());
      return pj ? `data:${pj.mimeType || 'application/octet-stream'};base64,${enBase64(pj.content)}` : m;
    });
  } else {
    html = `<pre class="eml-texte">${echapper(mail.text || '(message vide)')}</pre>`;
  }
  const img = imagesDistantes ? 'data: https: http:' : 'data:';
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${img}; style-src 'unsafe-inline'; font-src data:">
<meta name="color-scheme" content="light"><base target="_blank">
<style>html{background:Canvas;color:CanvasText}body{margin:16px;font:14px/1.5 system-ui,sans-serif;overflow-wrap:anywhere}
img{max-width:100%;height:auto}.eml-texte{white-space:pre-wrap;font:inherit;margin:0}</style>
</head><body>${html}</body></html>`;
}

export default function EmlApercu({ url }) {
  const [mail, setMail] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [imagesDistantes, setImagesDistantes] = useState(false);

  useEffect(() => {
    let annule = false;
    setMail(null); setErreur(null); setImagesDistantes(false);
    (async () => {
      try {
        const [{ default: PostalMime }, rep] = await Promise.all([import('postal-mime'), fetch(url)]);
        if (!rep.ok) throw new Error(`HTTP ${rep.status}`);
        const lu = await PostalMime.parse(await rep.arrayBuffer());
        if (!annule) setMail(lu);
      } catch (e) {
        if (!annule) setErreur(e.message || 'lecture impossible');
      }
    })();
    return () => { annule = true; };
  }, [url]);

  // Pièces jointes « visibles » : tout sauf les images déjà affichées dans le corps.
  const pieces = useMemo(() => {
    if (!mail) return [];
    const html = mail.html || '';
    return (mail.attachments || [])
      .filter((pj) => !(pj.contentId && pj.disposition !== 'attachment' && html.includes(`cid:${pj.contentId.replace(/^<|>$/g, '')}`)))
      .map((pj) => ({ ...pj, url: URL.createObjectURL(new Blob([pj.content], { type: pj.mimeType || 'application/octet-stream' })) }));
  }, [mail]);
  useEffect(() => () => pieces.forEach((p) => URL.revokeObjectURL(p.url)), [pieces]);

  const srcDoc = useMemo(() => (mail ? corpsIframe(mail, imagesDistantes) : ''), [mail, imagesDistantes]);
  const aDesImagesDistantes = !!mail?.html && /<img[^>]+src\s*=\s*["']?https?:/i.test(mail.html);

  if (erreur) return <div className="eml-msg">Aperçu impossible ({erreur}). Utilisez « Télécharger ».</div>;
  if (!mail) return <div className="eml-msg">Lecture de l’e-mail…</div>;

  const lignes = [
    ['De', adresse(mail.from)],
    ['À', adresses(mail.to)],
    ['Cc', adresses(mail.cc)],
    ['Date', dateLisible(mail.date)],
  ].filter(([, v]) => v);

  return (
    <div className="eml">
      <div className="eml-entete">
        <div className="eml-objet">{mail.subject || '(sans objet)'}</div>
        <dl className="eml-champs">
          {lignes.map(([k, v]) => (<React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>))}
        </dl>
        {pieces.length > 0 && (
          <div className="eml-pj">
            {pieces.map((p, i) => (
              <a key={i} href={p.url} download={p.filename || `piece-jointe-${i + 1}`} className="eml-pj-item" title="Télécharger la pièce jointe">
                📎 {p.filename || `Pièce jointe ${i + 1}`} <span>{taille(p.content?.byteLength || 0)}</span>
              </a>
            ))}
          </div>
        )}
        {aDesImagesDistantes && !imagesDistantes && (
          <div className="eml-images">
            Images distantes masquées : elles peuvent prévenir l’expéditeur que le message a été ouvert.
            <button type="button" onClick={() => setImagesDistantes(true)}>Afficher les images</button>
          </div>
        )}
      </div>
      <iframe className="eml-corps" title="Contenu de l’e-mail" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={srcDoc} />
    </div>
  );
}
